import { get, patch, postFormData, postJson, put, deleteRequest } from "./apiClient";
import type { InvoicePackingAlert } from "./packingAlertsApi";
import type { PaymentOnReceiptInput, PaymentOnReceiptStatus } from "../utils/paymentOnReceipt";
import type { VendorInvoiceDestination } from "./vendorInvoiceDestinationApi";
import type { VendorOption } from "./vendorsApi";
// The one owner of "a finite number greater than zero, or null" — never a
// second copy (CLAUDE.md's golden rule: duplicated num()s are this
// codebase's #1 historical defect source). Reused here, not re-derived, for
// `SupplierDropdownItem.unitPrice` below.
//
// F12 (owner decisions 2026-09-25): imported from `utils/priceUtils`, not
// `features/quotations/linePriceApplication` — this `lib/` file must not
// depend on a feature folder. Still the one implementation: that file now
// re-exports the same function from here.
import { positiveNumber } from "../utils/priceUtils";
// The one owner of "what does one catalogue row cost per kg / L / item"
// (BUSINESS_RULES#catalogue-row-unit-cost-ttd). `SyncPreviewCard.priceFacts`
// below is typed against its OWN input shape rather than a second copy of the
// same field list, so the two cannot drift apart.
import type { CostTierItem } from "../features/catalogue/costPriceTiers";

// A second `calculateSimilarity` used to live here, scoring item descriptions for
// a client-side supplier dropdown that no longer exists. It was not the server's
// rule with a different name — it was a different rule: its substring bonus sat
// AFTER the zero-overlap early return, so "GREASE" against "GREASES" scored 0.05
// here and 0.9 in `services/supplierOptions.service.js`. Both it and its only
// caller (`fetchSupplierDropdownData`) are gone; `/api/suppliers/dropdown` owns
// the matching, and `fetchSupplierDropdownDataFromAllTables` is how you ask it.

export type VendorInvoiceStatus = "pending" | "done" | "failed" | "processing" | "skipped";

export type VesselAssignment = {
  vesselName?: string | null;
  stickerColor?: string | null;
  /**
   * The CLIENT ORDER these goods were bought for (B6) — an id, because a vessel
   * name cannot identify an order: one client splits one ship's delivery across
   * several purchase orders by department so the owner and the charterer are
   * billed separately. This is the link that carries the client's PO number to
   * the client invoice. When it is set, `vesselName` above is not typed — it
   * follows the order.
   */
  confirmedOrderId?: string | null;
  assignedAt?: string | null;
  assignedBy?: string | null;
};

/** One vessel's share of a split invoice, as stored on the invoice itself. */
export type StoredVesselAllocation = {
  _id: string;
  vesselName: string;
  stickerColor?: string;
  label?: string;
  lines?: Array<{ index: number; quantity: number | null; basis: "packs" | "units" | "whole" }>;
  inventorySentAt?: string | null;
  /** D113.1 — lines on the vessel after the latest send. null on legacy rows. */
  inventorySentLines?: number | null;
  /** D113.1 — lines that send had to send (zero-quantity lines excluded). null on legacy rows. */
  inventoryLinesToSend?: number | null;
};

/**
 * Another invoice that names the same invoice number and vendor as this one.
 *
 * Not the same thing as the upload-time file hash, which is exact and rejects
 * outright. This is the same invoice on different paper — a re-scan, a
 * re-export, a PDF re-saved under a new name — so it is advisory, and
 * `inventoryConfirmedAt` is the field that decides how loud to be about it.
 */
export type DuplicateInvoice = {
  _id: string;
  fileName?: string;
  invoiceNumber?: string;
  invoiceDate?: string | null;
  dueDate?: string | null;
  vendorName?: string;
  /** Resolved server-side by the currency owner; the totals below are in it. */
  currency?: string;
  subTotal?: number | null;
  taxTotal?: number | null;
  grandTotal?: number | null;
  lineCount?: number | null;
  status?: string;
  inventoryConfirmedAt?: string | null;
  uploadedAt?: string | null;
};

export type VendorInvoice = {
  _id: string;
  status: VendorInvoiceStatus;
  vendorId?: string | null;
  attempts?: number;
  lastError?: string | null;
  createdAt?: string;
  updatedAt?: string;
  startedAt?: string;
  finishedAt?: string;
  source?: {
    kind?: string;
    fileName?: string;
    filePath?: string;
    fileSize?: number;
    fileSha256?: string;
    folderTag?: string;
    gcsBucket?: string | null;
    gcsObjectPath?: string | null;
    gcsUrl?: string | null;
    mimeType?: string | null;
    // D94: 1-based pages of the uploaded file this record was read from; non-empty
    // only on a record split off a file that held several invoices.
    pages?: number[];
  };
  // D94: a file that holds 2+ invoices asks the reviewer to split it (status
  // "pending"); "split" / "declined" record the answer. A pending question does
  // not block Confirm & sync.
  splitProposal?: {
    invoices: { invoiceNumber: string | null; pages: number[] }[];
    status: "" | "pending" | "split" | "declined";
    decidedAt?: string | null;
    decidedBy?: string | null;
  } | null;
  extracted?: any;
  meta?: any;
  assignment?: VesselAssignment;
  // CR-14 — verified state; when set, UI clears "Calculated Discrepancy" badge
  verifiedAt?: string | null;
  verifiedBy?: string | null;
  // Dotted `extracted.*` paths a person has changed by hand — drives the blue
  // "manually edited" cell highlight on the review screen.
  manuallyEditedPaths?: string[];
  // Dotted `extracted.*` paths a person has checked against the document and
  // vouched for WITHOUT changing — the answer to an amber cell whose value was
  // already right. Turns the cell blue like a manual edit does.
  reviewConfirmedPaths?: string[];
  // UC5 — set once a person has pressed "Confirm & sync to Inventory". Before
  // this, nothing on the invoice has touched Inventory or the Master
  // Catalogue, however long ago it finished processing.
  inventoryConfirmedAt?: string | null;
  inventoryConfirmedBy?: string | null;
  // D95: where the cost lands. Missing = 'united' (read through destinationOf).
  destination?: VendorInvoiceDestination;
  // Was this invoice already paid when it arrived? (D49.) Asked at upload, kept
  // here until Confirm & sync raises the bill and applies it. Absent on an invoice
  // uploaded before the question existed, which Confirm & sync refuses until it is
  // answered. `amount` is the part-paid amount only.
  paymentOnReceipt?: {
    status: PaymentOnReceiptStatus;
    paidDate?: string | null;
    method?: string;
    reference?: string;
    amount?: number | null;
    answeredAt?: string | null;
  } | null;
  // Set only when Confirm & sync was pushed past a duplicate refusal.
  duplicateOverrideAt?: string | null;
  duplicateOverrideBy?: string | null;
  // Set when the duplicate check could not RUN and the confirm went through
  // without it (TECH_DEBT #55). Distinct from the override above: that one
  // records a human deciding the twin is a different invoice, this one records
  // nobody having been asked. Not rendered yet — carried so a screen that wants
  // to flag "stocked in without a duplicate check" has the fact available
  // rather than having to trust that a clean check happened.
  duplicateCheckFailedAt?: string | null;
  duplicateCheckFailedReason?: string | null;
  // Soft dedupe — the same INVOICE on different paper, which the file hash
  // cannot see. `duplicateInvoices` comes back on the single-invoice read only;
  // the list carries the two counts instead, to keep it one query per page.
  dedupe?: { invoiceNumberKey?: string | null; vendorKey?: string | null };
  duplicateInvoices?: DuplicateInvoice[];
  duplicateCount?: number;
  duplicateConfirmedCount?: number;
  // REC-1 — attached POs
  purchaseOrderIds?: Array<{ poId: string; poNumber?: string; matchSource?: 'auto' | 'manual' | ''; matchedAt?: string | null }>;
  // D1 — which vessels this invoice's goods go to. Empty on an unsplit invoice,
  // one entry per destination once it has been split. The invoice itself is
  // never cloned, so this is the only place the extra vessels appear.
  vesselAllocations?: StoredVesselAllocation[];
  // DEPRECATED — set only on child invoices produced by the pre-2026-08 split,
  // which cloned the document. Kept so those records still render.
  splitFromInvoiceId?: string | null;
  splitLabel?: string;
  vendorReconciliation?: {
    state?: "unmatched" | "suggested" | "matched" | "resolved";
    resolution?: string;
    matchedVendorId?: string | null;
    matchedVendorName?: string | null;
    bestCandidate?: any;
    candidates?: any[];
    reviewedAt?: string | null;
    resolvedAt?: string | null;
    note?: string | null;
    extractedVendor?: any;
  };
  /**
   * Prod USC-BUG-0028 — the lines to ask "are these fixed 2 KG packs?": a
   * catch-weight line whose Unit Size is a weight, not yet answered. Worked out
   * on the server by the owners of both halves (`caseWeights.isCatchWeightLine`,
   * `pricingBasis.getUnitCategory`), which have no client twin. On the list and
   * on the single-invoice read.
   */
  fixedPackWeightQuestions?: FixedPackWeightQuestion[];
  /**
   * D29 / MC-5 — "check the packing", per line index. Worked out on the server
   * (`packingAlert.service.packingAlertsForInvoice`). On the list AND on the
   * single-invoice read: the review modal renders the LIST row, so a list that
   * dropped these left the alert visible only in the Master Catalogue.
   */
  packingAlerts?: Record<number, InvoicePackingAlert[]>;
};

/** One line's "are these fixed 2 KG packs?" question (prod USC-BUG-0028). */
export type FixedPackWeightQuestion = {
  /** Position of the line in `extracted.lineItems`. */
  index: number;
  /** The weight to ask about, as the catalogue would store it: "2 KG". */
  size: string;
  /** The Unit Size and its reading the question was worked out from, so a line retyped since can be told apart. */
  unitSize: string | null;
  reading: string | null;
};

export type VesselStickerMapping = {
  _id: string;
  vesselName: string;
  stickerColor: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
};

/**
 * Sortable list columns. The server owns this list (see utils/vendorInvoiceSort.js)
 * and falls back to newest-first for anything it doesn't recognise.
 */
export type VendorInvoiceSortKey =
  | "createdAt"
  | "updatedAt"
  | "invoiceNumber"
  | "vendor"
  | "vessel"
  | "total"
  | "items"
  | "status"
  | "confidence";

export type SortDir = "asc" | "desc";

export type VendorInvoiceListResponse = {
  items: VendorInvoice[];
  page: number;
  limit: number;
  total: number;
  stats?: Record<string, number>;
  /** The order actually applied — may differ from what was asked for. */
  sort?: VendorInvoiceSortKey;
  dir?: SortDir;
};

export async function listVendorInvoices(params: {
  page?: number;
  limit?: number;
  status?: VendorInvoiceStatus | "all";
  q?: string;
  tag?: string;
  vessel?: string;
  /** D95: one destination, or blank for all. */
  destination?: VendorInvoiceDestination | "";
  /** Pickers that cost a client's items or match a PO: never offer a company expense. */
  excludeExpense?: boolean;
  sort?: VendorInvoiceSortKey;
  dir?: SortDir;
}) {
  const { page = 1, limit = 25, status = "all", q = "", tag = "", vessel = "", destination = "", excludeExpense = false, sort, dir } = params;

  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("limit", String(limit));
  if (status !== "all") qs.set("status", status);
  if (q) qs.set("q", q);
  if (tag) qs.set("tag", tag);
  if (vessel) qs.set("vessel", vessel);
  if (destination) qs.set("destination", destination);
  if (excludeExpense) qs.set("excludeExpense", "1");
  if (sort) qs.set("sort", sort);
  if (dir) qs.set("dir", dir);

  return get<VendorInvoiceListResponse>(`/vendor-invoices?${qs.toString()}`);
}

// ─── Module 1: vessel assignment ──────────────────────────────────

/**
 * @param payload `confirmedOrderId` is three-state (B6). Omit the key to leave
 *   an existing link alone; send `null` to unlink deliberately; send an id to
 *   link. `vesselName` may be omitted when an order is linked — the vessel is
 *   then the order's, resolved by the server through the one owner of what a
 *   vessel is called. A vesselName sent WITH a link is treated as a correction
 *   to the ORDER's vessel and cascades to the RFQ, the quotation and the POs.
 */
export async function assignVendorInvoiceVessel(
  id: string,
  payload: { vesselName?: string; stickerColor: string; confirmedOrderId?: string | null }
) {
  // `attachedPo`: the PO the tag attached the bill to by itself, or null (D171.3).
  return patch<{ success: boolean; data: VendorInvoice & { attachedPo?: AttachedPoNote | null } }>(`/vendor-invoices/${id}/assignment`, payload);
}

/**
 * Copy for a failed vessel/order save, keyed on the envelope's `code`
 * (invariant 6) — never on the server's prose, which a copy edit would break.
 */
export function vesselAssignmentErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;

  switch (code) {
    case 'VESSEL_NAME_REQUIRED':
      return 'This order has no vessel on it yet, so there is nothing to print on the sticker. Type the vessel name here — it will be saved onto the order too.';
    case 'CONFIRMED_ORDER_NOT_FOUND':
    case 'CONFIRMED_ORDER_ID_INVALID':
      return 'That confirmed order could not be found. Reload the page and pick it again.';
    case 'STICKER_COLOR_REQUIRED':
      return 'Pick a sticker colour before saving.';
    case 'STICKER_COLOR_INVALID':
      return 'That sticker colour is not one of the allowed colours. Pick one from the swatches.';
    case 'VENDOR_INVOICE_NOT_FOUND':
      return 'This vendor invoice no longer exists. Reload the page.';
    default:
      return 'The assignment could not be saved. Nothing has been changed — try again, and contact support if it keeps failing.';
  }
}

export async function clearVendorInvoiceAssignment(id: string) {
  return deleteRequest<{ success: boolean; data: VendorInvoice }>(`/vendor-invoices/${id}/assignment`);
}

export async function listVesselStickerMappings() {
  return get<{ success: boolean; data: { mappings: VesselStickerMapping[]; allowedColors: string[] } }>(
    `/vendor-invoices/vessel-stickers`
  );
}

export async function suggestStickerColorForVessel(vesselName: string) {
  const qs = new URLSearchParams({ vesselName });
  return get<{ success: boolean; data: { stickerColor: string | null } }>(
    `/vendor-invoices/vessel-stickers/suggest?${qs.toString()}`
  );
}

export async function patchVendorInvoice(
  id: string,
  ops: { path: string; value: any }[],
  opts?: { manual?: boolean }
) {
  return patch<{
    ok: boolean;
    matched?: number;
    modified?: number;
    /**
     * The vendor item memory filled or corrected cells this request never sent.
     *
     * It happens when an edit NAMES a product — typing the item name gives the
     * line an identity that survives a misreading, so a supplier whose invoices
     * are handwritten resolves to what a reviewer settled on an earlier one
     * (Lawrence Marketing 25622 / 26129). The grid has to reload to show them:
     * they are not in `ops`, so nothing on this side can infer they arrived.
     */
    memoryApplied?: boolean;
    /**
     * The edit touched a Unit Size or how it is read, so the invoice's
     * `fixedPackWeightQuestions` (prod USC-BUG-0028), which the server works out,
     * may no longer be what it sent. The grid reloads on this rather than on a
     * copy of that rule's field list.
     */
    unitSizeQuestionsMayHaveChanged?: boolean;
    /**
     * The server worked out again (or cleared) the Unit Price of a line whose
     * price came from its printed total — a Line Total edit does that, and
     * `lineTotal` is in no shared field list the grid reloads on.
     */
    derivedPriceChanged?: boolean;
  }>(`/vendor-invoices/${id}`, { ops, manual: opts?.manual !== false });
}

/**
 * Remove one line from an invoice still under review (prod USC-BUG-0021).
 *
 * `expected` is the row as the grid showed it; the server refuses when it no
 * longer describes the line at `index` (a stale screen), once the invoice is in
 * Inventory, and while it is split across vessels or matched to a PO. The line
 * is kept on the invoice as a record, not on the grid.
 */
export async function removeVendorInvoiceLine(
  id: string,
  index: number,
  expected: { lineNo?: unknown; description?: unknown; itemName?: unknown }
) {
  return postJson<{ success: boolean; data: { ok: boolean; removedIndex: number; lineCount: number } }>(
    `/vendor-invoices/${encodeURIComponent(id)}/lines/${index}/remove`,
    { expected }
  );
}

// ─── D1 — split across vessels, then send to inventory ────────────

/**
 * How much of one line a vessel takes.
 *
 * `packs` counts the outer unit (the grid's Quantity / Pack Unit columns);
 * `units` counts the inner one (Total Units / Unit). The two are not
 * interchangeable, so the basis travels with the number rather than being
 * inferred at the far end.
 */
export type SplitLineAllocation = {
  index: number;
  quantity: number;
  basis: "packs" | "units";
};

export type VendorInvoiceSplitSpec = {
  vesselName: string;
  stickerColor?: string;
  splitLabel?: string;
  /** Whole lines. Mutually exclusive with `lines` for any given line. */
  lineIndices?: number[];
  /** Part of a line. Quantities for one line may total LESS than the line has —
   *  the rest simply stays in the warehouse. More is rejected. */
  lines?: SplitLineAllocation[];
};

/** A vessel's share, with the quantities worked out from the spec. */
export type VesselSplitAllocation = {
  _id: string | null;
  vesselName: string;
  stickerColor?: string;
  label?: string;
  /** What was asked for — enough to rebuild the split form exactly as saved. */
  spec: Array<{ index: number; quantity: number | null; basis: "packs" | "units" | "whole" }>;
  /** Line items carrying this vessel's apportioned quantities. Not persisted. */
  lines: any[];
  lineCount: number;
  /** Fraction of the invoice's line value, 0..1. */
  share: number | null;
  value: number | null;
  inventorySentAt?: string | null;
  inventorySentLines?: number | null;
  inventoryLinesToSend?: number | null;
};

/** What is allocated and what stays behind, per line of the invoice. */
export type SplitLineRemainder = {
  index: number;
  basis: "packs" | "units" | "whole";
  total: number | null;
  allocated: number;
  remaining: number | null;
  vessels: number;
};

export type VesselSplitResult = {
  allocations: VesselSplitAllocation[];
  remainders: SplitLineRemainder[];
  /** Set when a stored split no longer matches the invoice's line items. */
  error?: string | null;
};

/**
 * Record how this invoice's goods are split across vessels.
 *
 * Does not create child invoices — the invoice stays one payable and grows a
 * list of destinations. Saving again replaces the previous split.
 */
export async function splitVendorInvoiceByVessel(id: string, splits: VendorInvoiceSplitSpec[]) {
  return postJson<{ success: boolean; data: VesselSplitResult & { invoice: VendorInvoice } }>(
    `/vendor-invoices/${id}/split-by-vessel`,
    { splits }
  );
}

/** Read a saved split back, with quantities recomputed against the invoice as it stands. */
export async function getVendorInvoiceVesselSplit(id: string) {
  return get<{ success: boolean; data: VesselSplitResult }>(
    `/vendor-invoices/${id}/split-by-vessel`
  );
}

/** Forget the split. Inventory movements it already produced are left alone. */
export async function clearVendorInvoiceVesselSplit(id: string) {
  return deleteRequest<{ success: boolean; data: { invoice: VendorInvoice } }>(
    `/vendor-invoices/${id}/split-by-vessel`
  );
}

export type VesselAllocationLine = {
  lineNumber: number;
  itemName: string;
  status: "allocated" | "already_allocated" | "skipped";
  reason?: string;
  /** Machine-readable refusal code on a skipped line (e.g. VESSEL_SEND_LINE_FAILED). */
  code?: string;
  movementId?: string | null;
  /** On an already_allocated line: the existing movement's status. Only 'applied' means the goods left. */
  movementStatus?: string | null;
  level1Qty?: number;
  level2Qty?: number;
};

export type VesselAllocationResult = {
  vesselName: string;
  stickerColor?: string;
  allocated: number;
  alreadyAllocated: number;
  skipped: number;
  /** D112.3 — lines whose goods are on the vessel after this run (movement APPLIED). */
  applied: number;
  /** D113.1 — lines that had something to send (zero-quantity lines excluded). */
  toSend: number;
  lines: VesselAllocationLine[];
};

/** D113.1 — the facts a vessel allocation stores about its sends. */
export type VesselSendFacts = Pick<StoredVesselAllocation, "inventorySentAt" | "inventorySentLines" | "inventoryLinesToSend">;

export type VesselSendState =
  | { kind: "sent" }
  | { kind: "partly"; sent: number; toSend: number }
  | { kind: "unsent" };

/**
 * D113.1 (owner, 2026-10-12) — the ONE place that decides whether a split
 * vessel is sent. "Sent" only when every line that had something to send is
 * on the vessel; 9 of 10 is "partly", and Send stays available for the rest
 * (a re-send skips lines already gone). A stamp with no counts is a row sent
 * before the counts were stored, and reads as sent, as it always did.
 */
export function vesselSendState(a: VesselSendFacts | null | undefined): VesselSendState {
  if (!a?.inventorySentAt) return { kind: "unsent" };
  const sent = a.inventorySentLines;
  const toSend = a.inventoryLinesToSend;
  if (typeof sent !== "number" || typeof toSend !== "number") return { kind: "sent" };
  return sent >= toSend ? { kind: "sent" } : { kind: "partly", sent, toSend };
}

export function vesselSendLabel(state: VesselSendState): string {
  if (state.kind === "sent") return "Sent";
  if (state.kind === "partly") return `Partly sent (${state.sent} of ${state.toSend})`;
  return "Not yet sent";
}

/**
 * D112.3 — did this send leave the vessel's goods out of stock? Reads the
 * server's `applied` count only: an `already_allocated` line whose earlier
 * movement never applied moved nothing, so `allocated + alreadyAllocated`
 * would call a stuck send "sent". A missing or non-numeric `applied` is not sent.
 */
export function vesselSendMovedStock(result: VesselAllocationResult | null | undefined): boolean {
  const applied = result?.applied;
  return typeof applied === "number" && applied > 0;
}

/**
 * Release goods to a vessel.
 *
 * Allocates stock that is already on the books — it does not receive any. So
 * 15kg received with 5kg sent to each of two vessels leaves 5kg on hand.
 *
 * `allocationId` sends one vessel's share of a split invoice. Without it, the
 * whole invoice goes to the single vessel it is assigned to.
 *
 * Safe to call twice: lines already allocated to that vessel come back as
 * `already_allocated`.
 */
export async function allocateVendorInvoiceToVessel(id: string, allocationId?: string | null) {
  return postJson<{ success: boolean; data: VesselAllocationResult }>(
    `/vendor-invoices/${id}/allocate-to-vessel`,
    allocationId ? { allocationId } : {}
  );
}

/** D113.10 — what cancelling one vessel's send did. */
export type VesselSendCancelResult = {
  vesselName: string;
  /** Lines whose goods came back off the vessel. */
  reversed: number;
  /** Where they went back to (`united` / `pantry`). */
  locations: string[];
  /** Old stuck sends that never applied: moved nothing, left as they are. */
  notApplied: Array<{ movementId: string; lineNumber: number | null; status: string }>;
};

/**
 * D113.10 (owner, 2026-10-12) — cancel one vessel's send. Its goods go back to
 * the stock they left and the vessel reads "Not yet sent", or nothing changes.
 * Refusals carry a code: see vesselSendCancelErrorMessage.
 */
export async function cancelVendorInvoiceVesselSend(id: string, allocationId: string) {
  return postJson<{ success: boolean; data: VesselSendCancelResult }>(
    `/vendor-invoices/${id}/vessel-allocations/${allocationId}/cancel-send`,
    {}
  );
}

/** Copy for a cancel-send refusal, keyed on the server's `code`, never its prose. */
export function vesselSendCancelErrorMessage(err: unknown): string {
  const e = err as { code?: unknown; details?: { creditNotes?: unknown } } | null;
  const notes = Array.isArray(e?.details?.creditNotes) ? (e?.details?.creditNotes as string[]) : [];
  switch (e?.code) {
    case 'VESSEL_SEND_HELD_BY_CREDIT_NOTE':
      return `${notes.length ? `Credit note ${notes.join(', ')}` : 'A credit note'} names goods from this send, so it cannot be cancelled. Cancel or change that credit note first.`;
    case 'VESSEL_SEND_HELD_BY_VESSEL_MOVE':
      return 'Some of these goods have since been returned from the vessel or moved to another vessel, so this send cannot be cancelled here. Correct the stock in Inventory instead.';
    case 'VESSEL_ALLOCATION_NOT_FOUND':
      return 'This vessel is no longer part of the split. Reopen the split and try again.';
    case 'VENDOR_INVOICE_NOT_FOUND':
      return 'This invoice no longer exists.';
    case 'STOCK_LAYER_MISSING':
      return 'A stock batch or box these goods came from no longer exists, so they cannot go back into it. Nothing was changed. Correct the stock in Inventory instead.';
    default:
      return 'Could not cancel the send. Nothing was changed. Try again.';
  }
}

export async function reprocessVendorInvoice(id: string) {
  return postJson(`/vendor-invoices/${id}/reprocess`, {});
}

/**
 * CR-14 — a human has checked these figures; stop showing the discrepancy badge.
 *
 * Through apiClient like every other call on this page. The review grid used to
 * call this route with a bare `fetch` against
 * `process.env.REACT_APP_API_BASE` — a variable that exists nowhere in this
 * project (the configured one is REACT_APP_API_BASE_URL), so the URL always
 * collapsed to the relative "/api". In development that hit the CRA dev server,
 * which has no proxy, and in a split frontend/backend deployment it hit the
 * frontend host instead of the injected API base. The button simply did
 * nothing, on every environment except same-origin production.
 */
export async function markVendorInvoiceVerified(id: string) {
  return postJson(`/vendor-invoices/${id}/verify`, {});
}

/**
 * "I have checked these flagged cells against the document and they are right."
 *
 * The counterpart to the amber highlight on the review grid. An amber cell
 * holds a figure the extractor could not prove — a VAT rate it defaulted to,
 * an amount it worked out because the invoice printed none — and its tooltip
 * asks for it to be confirmed. Typing the same value back in is not an answer:
 * EditableCell treats an unchanged value as a cancel, so a figure that was
 * already right could not be confirmed at all.
 *
 * `paths` is what the grid was showing as amber when the button was pressed.
 * Sent whole rather than one cell at a time, because a reviewer holding the
 * paper reads the invoice in one pass.
 */
export async function confirmVendorInvoiceReview(id: string, paths: string[]) {
  return postJson<{ success: boolean; data: { ok: boolean; confirmed: number; paths: string[] } }>(
    `/vendor-invoices/${id}/confirm-review`,
    { paths }
  );
}

/**
 * UC5 — the only way an invoice's lines reach Inventory / the Master
 * Catalogue. The server refuses (throws) if the invoice does not reconcile
 * with its own grand total yet, if it was already confirmed once, or if an
 * invoice with the same number and vendor has already been stocked in.
 *
 * `allowDuplicate` answers only that last refusal — it is what the "Confirm
 * anyway" button sends, and it is recorded on the invoice when used. It has no
 * effect on the arithmetic or already-confirmed gates.
 */
/** One row of a change card: what a field says now, and what it would say. */
export type SyncPreviewField = {
  key: string;
  label: string;
  from: string | null;
  to: string | null;
  changed: boolean;
  /**
   * A price the sync will NOT write — what the invoice states, beside the price
   * the row keeps. Present on a parked or held line only.
   */
  proposed?: string | null;
  changePct?: number | null;
  note?: string | null;
};

/**
 * `CostTierItem` plus the two fields only `altPricingFor` needs: the item's
 * own NAME, for the "pack size read out of the name" derivation, and the
 * server's `displayEquivalents` snapshot (`meta_data.pricingDetails` on the
 * catalogue row) that derivation reads first. Both widened onto
 * `SyncPreviewCard.priceFacts` / `priorPriceFacts` 2026-09-25, alongside
 * `altPricingFor` itself — see its doc comment for why the card needs them:
 * without `item_name` a NAME-priced pack ("CORN FLAKES 24 X 300G") showed the
 * Master Catalogue grid's Per Pack figure and nothing on this card.
 */
export type SyncPreviewPriceFacts = CostTierItem & {
  item_name?: string | null;
  pricingDetails?: Record<string, unknown> | null;
};

/** One catalogue item this invoice touches. */
export type SyncPreviewCard = {
  lineIndex: number;
  itemName: string | null;
  catalogueId: string | null;
  outcome: "new" | "update" | "parked" | "kept" | "excluded";
  reason: string | null;
  changePct: number | null;
  excluded: boolean;
  changedCount: number;
  fields: SyncPreviewField[];
  /**
   * The raw facts the card's price block needs to draw the SAME per-kg / per-L
   * / per-item figures the Master Catalogue grid would show for the row IF
   * the sync leaves it in place — `costPriceTiersTtd` reads them, never a
   * second computation here (the owner, 2026-09-25: a reviewer typed Units /
   * Case 12 on a "White Quinoa, 5kg Bag" and nothing on this card said the row
   * now priced at TT$0.36/kg). `null` only when the plan line carries neither
   * a doc nor a prior row to price from.
   */
  priceFacts: SyncPreviewPriceFacts | null;
  /**
   * The SAME facts, for the row AS IT STANDS RIGHT NOW — read instead of
   * `priceFacts` when the reviewer excludes the line, because then nothing is
   * written and the block has to say what the row still holds, not what the
   * sync would have set it to (2026-09-25). `null` for a brand-new row: there
   * is no prior catalogue row to read, and the card says so in words instead.
   */
  priorPriceFacts: SyncPreviewPriceFacts | null;
  /**
   * The SERVER excludes this line regardless of the switch — a credit note, or
   * a packaging-unchecked line — and nothing is written, so `priceFacts` equals
   * `priorPriceFacts`: the row as it stands (null for a new row). Optional:
   * an older server that does not send it means "no", the same as `false`.
   */
  forcedExcluded?: boolean;
  /** Why, in words, when `forcedExcluded` is true. Null otherwise. */
  forcedExcludedReason?: string | null;
  /**
   * D110.3: the category the AI picked from the existing ones for a NEW row.
   * Non-null `categorySuggestion` only on an included new card: 'ai' = a pick is
   * present, 'none' = the AI had no valid pick, 'unavailable' = AI off or failed.
   * Optional: an older server sends neither, and the card shows no picker.
   */
  suggestedCategory?: { key: string; label: string } | null;
  categorySuggestion?: "ai" | "unavailable" | "none" | null;
};

export type SyncPreview = {
  invoiceId: string;
  invoiceNumber: string | null;
  vendorName: string | null;
  alreadyConfirmed: boolean;
  computedAt: string;
  /**
   * What the confirm would refuse. None is overridable since the unit gate lost
   * its Confirm anyway (2026-09-14, prod USC-BUG-0022 / 0026); `overridable` is
   * kept so a panel talking to an older server still reads its answer.
   */
  refusals: {
    code: string;
    message: string;
    overridable?: boolean;
    lines: { lineNo: number; detail: string }[];
  }[];
  excludedLines: number[];
  counts: {
    created: number;
    repriced: number;
    heldOlder: number;
    parked: number;
    excluded: number;
    merged: number;
    skipped: number;
    stranded: number;
  };
  cards: SyncPreviewCard[];
  /** D110.3: the category vocabulary a new row can be filed under. */
  categories?: { key: string; label: string }[];
  skippedLines: { lineIndex: number; reason: string }[];
  mergedLines: { lineIndex: number; wonByLineIndex: number }[];
  /** Rows the sync will DELETE, left behind by a renamed line. */
  removedRows: { id: string; itemName: string | null }[];
  /** What each row said when this was computed, so the confirm can refuse a stale approval. */
  previewedRows: { id: string; signature: string }[];
};

/**
 * What Confirm & sync would change in the Master Catalogue, without changing it.
 *
 * A dry run of the real sync on the server, so the cards cannot promise
 * something the sync would not do. Needs permission to edit Vendors AND to view
 * Products, because the cards show catalogue costs — a caller without both gets
 * a 403 and keeps the plain confirmation dialog.
 */
export async function getVendorInvoiceSyncPreview(id: string) {
  return get<{ success: boolean; data: SyncPreview }>(
    `/vendor-invoices/${id}/confirm-inventory/preview`
  );
}

// ─── VI-7 (D29) — correct a stocked line's packaging ────────────────

/** The three cells a correction states. Unit Size carries its unit word on the server. */
export type PackagingCorrectionFields = {
  splitUow?: string | null;
  splitPackCount?: number | string | null;
  splitUnitSize?: string | null;
};

export type PackagingLineCells = {
  splitUow: string | null;
  splitPackCount: number | null;
  splitUnitSize: string | null;
  splitItemUow: string | null;
  splitItemUnits: number | null;
};

export type PackagingPriceTiers = {
  price: number | null;
  currency: string | null;
  unit: string | null;
  perPack: number | null;
  perUnit: number | null;
  perKg: number | null;
  perLb: number | null;
  perL: number | null;
  byUnit: { unit: string; ttAmount: number | null }[];
} | null;

export type PackagingCorrectionPreview = {
  invoiceId: string;
  invoiceNumber: string | null;
  lineIndex: number;
  lineNo: number;
  itemName: string | null;
  fxRate: number | null;
  line: { before: PackagingLineCells; after: PackagingLineCells };
  catalogue: {
    /** `apply` / `insert` price the row; `hold` / `park` write packaging only. */
    outcome: string;
    reason?: string | null;
    changePct?: number | null;
    identity?: { from: string | null; to: string | null; changed: boolean };
    conversions?: { before: string[]; after: string[]; replaced: string[]; conflicts: string[] };
    prices?: { before: PackagingPriceTiers; after: PackagingPriceTiers; proposed: PackagingPriceTiers };
    rowsAffected: { id: string | null; itemName: string | null; effect: string }[];
  };
  /** `sharedCodeWith`: the other product this line's supplier code also stands for; memory is then left alone. */
  memory: { weight: string; sharedCodeWith?: string | null; fields: { field: string; from: unknown; to: unknown }[] };
};

export type PackagingCorrectionResult = {
  invoiceId: string;
  lineIndex: number;
  lineNo: number;
  line: { before: PackagingLineCells; after: PackagingLineCells };
  memory: { recorded: number; skipped: number; sharedCodeWith?: string | null };
  catalogue: {
    created: number;
    repriced: number;
    heldOlder: number;
    parked: number;
    relocated: number;
    replacedConversions: string[];
  };
};

/**
 * What correcting this line's packaging would change, writing nothing. Needs
 * Master Catalogue edit rights (403 otherwise).
 */
export async function previewPackagingCorrection(id: string, index: number, fields: PackagingCorrectionFields) {
  return postJson<{ success: boolean; data: PackagingCorrectionPreview }>(
    `/vendor-invoices/${encodeURIComponent(id)}/lines/${index}/packaging-correction/preview`,
    { fields }
  );
}

/**
 * Apply it. `expected` is the line as the preview showed it; a line changed
 * since is refused with PACKAGING_CORRECTION_STALE.
 */
export async function applyPackagingCorrection(
  id: string,
  index: number,
  fields: PackagingCorrectionFields,
  expected: Partial<PackagingLineCells>
) {
  return postJson<{ success: boolean; data: PackagingCorrectionResult }>(
    `/vendor-invoices/${encodeURIComponent(id)}/lines/${index}/packaging-correction/apply`,
    { fields, expected }
  );
}

/**
 * What Confirm & sync answers with. Only the field the screen has to word is typed; the rest
 * (`stockIn`, `inventory`, `catalogue`, `packingAlerts`, `destination`) is read as it always was.
 */
export interface ConfirmInventoryResult {
  /**
   * Present only when NO bill was raised for this invoice (D116.3): another invoice of the same
   * supplier and number already has one, so the stock is in and the money is owed on that bill.
   */
  billSkipped?: { reason: 'duplicate'; ofInvoiceId: string };
  /** Present only when the confirm attached the bill to its order's one PO by itself (D171.3). */
  attachedPo?: AttachedPoNote;
  [field: string]: any;
}

/** The PO a bill attached itself to (D171.3): its order's one PO from the bill's supplier. */
export interface AttachedPoNote {
  poId: string;
  poNumber: string;
}

export async function confirmVendorInvoiceInventory(
  id: string,
  opts?: {
    allowDuplicate?: boolean;
    confirmPaths?: string[];
    excludedLines?: number[];
    previewedRows?: { id: string; signature: string }[];
    /** D110.3: lineIndex -> category key for each included new row. Blank = no category. */
    categoryChoices?: Record<string, string>;
  }
) {
  return postJson<{ success: boolean; data: ConfirmInventoryResult }>(
    `/vendor-invoices/${id}/confirm-inventory`,
    {
      ...(opts?.allowDuplicate ? { allowDuplicate: true } : {}),
      // No unit override: "this line's Unit is a pack code, not a unit" has no
      // Confirm anyway since 2026-09-14 (prod USC-BUG-0022, 0026).
      // The flagged cells the reviewer was shown and left standing. Sent WITH the
      // sync, not by a confirm-review call before it: the server records them in
      // the same write that claims the invoice, so there is no state where the
      // stock moved and the confirmations were lost.
      ...(opts?.confirmPaths?.length ? { confirmPaths: opts.confirmPaths } : {}),
      // The change card's switches. Sent whenever the reviewer saw the cards,
      // INCLUDING when the list is empty: an empty list is how somebody clears
      // an exclusion an earlier confirm stored, and dropping it would leave that
      // line switched off for good.
      ...(opts?.excludedLines !== undefined ? { excludedLines: opts.excludedLines } : {}),
      // What those cards were drawn from. The server refuses with
      // SYNC_PREVIEW_STALE if one of these rows moved while they were reading.
      ...(opts?.previewedRows !== undefined ? { previewedRows: opts.previewedRows } : {}),
      // Like excludedLines: sent whenever the reviewer saw the cards, INCLUDING `{}` ("No category"
      // on every card). Omitting it would let the server fall back to a pick stamped earlier.
      ...(opts?.categoryChoices !== undefined ? { categoryChoices: opts.categoryChoices } : {}),
    }
  );
}

/** One goods receipt: what a single invoice line put into the warehouse. */
export type StockLayer = {
  _id: string;
  itemId: string;
  location: "united" | "pantry";
  qtyReceivedLevel1: number;
  qtyRemainingLevel1: number;
  qtyReservedLevel1: number;
  qtyAvailableLevel1?: number;
  expiryDate: string | null;
  receivedAt: string;
  costPerLevel1: number;
  currency: string;
  uomLevel1Name?: string;
  uomLevel2Name?: string;
  sourceVendorInvoiceId?: string | null;
  sourceInvoiceNumber?: string;
  sourceLineNumber?: number | null;
  sourceVendorName?: string;
  originalVesselName?: string;
  backfilled?: boolean;
  /**
   * The individual boxes, on catch-weight receipts only. Empty everywhere else.
   * A 119.5 kg line of ribeye is nine of these, weighing 14.40, 12.10, 13.00 …
   */
  cases?: Array<{
    seq: number;
    code: string;
    weightLevel1: number;
    weightRemainingLevel1: number;
    opened: boolean;
    status: "available" | "reserved" | "consumed";
    vesselName?: string;
  }>;
  /** Why a catch-weight receipt ended up with no boxes. */
  caseWeightsFlag?: string | null;
};

/**
 * Stock layers this invoice opened, one per line that actually stocked
 * something in. Lines with no layer received nothing.
 */
export async function getVendorInvoiceStockLayers(id: string) {
  return get<{ success: boolean; data: StockLayer[] }>(`/vendor-invoices/${id}/stock-layers`);
}

/**
 * Record when this line's goods expire.
 *
 * Expiry drives which stock gets consumed first, and nothing upstream captures
 * it — stock-in happens automatically when the invoice is processed, before
 * anyone has read the document. Pass null to clear a date entered by mistake.
 */
export async function setVendorInvoiceLineExpiry(
  id: string,
  lineNumber: number,
  expiryDate: string | null
) {
  return patch<{ success: boolean; data: StockLayer }>(
    `/vendor-invoices/${id}/lines/${lineNumber}/expiry`,
    { expiryDate }
  );
}

/**
 * Correct the individual case weights behind this line's goods.
 *
 * Catch-weight lines — meat, poultry, seafood — are billed by weight but
 * delivered as boxes of unequal weight. Getting these right is what lets a
 * vessel asking for 50 kg be told which boxes to carry out. An empty list
 * clears them, putting the receipt back to a plain quantity.
 *
 * Refused once a box has been reserved or shipped: by then the weights are a
 * record of goods that left, not a description of goods in the room.
 */
export async function setVendorInvoiceLineCaseWeights(
  id: string,
  lineNumber: number,
  weights: number[]
) {
  return patch<{ success: boolean; data: StockLayer }>(
    `/vendor-invoices/${id}/lines/${lineNumber}/case-weights`,
    { weights }
  );
}

export async function getVendorInvoiceById(id: string) {
  return get<{ success: boolean; data: VendorInvoice }>(`/vendor-invoices/${id}`);
}

/**
 * Mint a short-lived signed URL for the invoice's attachment. The attachment
 * route itself takes no auth header (it is opened via <a>/window.open), so the
 * signature in the returned URL IS the authorization — fetch this on click and
 * open it immediately; it expires in ~10 minutes. Returns a server-relative
 * path (`/api/...`): make it absolute with the caller's base-URL helper.
 */
export async function fetchVendorInvoiceAttachmentUrl(id: string): Promise<string> {
  const res = await get<{ success: boolean; data: { url: string; expiresAt: string } }>(
    `/vendor-invoices/${id}/attachment-url`
  );
  return res.data.url;
}

/**
 * Deleting also returns to inventory any stock the invoice added. When that
 * stock has already been transferred or issued the server refuses (409) rather
 * than leaving the counts wrong; `force` removes the invoice anyway and leaves
 * the stock in place.
 */
export async function deleteVendorInvoice(id: string, opts?: { force?: boolean }) {
  const qs = opts?.force ? "?force=true" : "";
  return deleteRequest(`/vendor-invoices/${id}${qs}`);
}

export async function reconcileVendorInvoice(
  id: string,
  payload: {
    // Linking only: vendors are added on the Vendors page (D86).
    action: "same_existing";
    vendorId?: string;
    note?: string;
    /** Opt-in: also update the matched vendor's phone/address/credit-days from this invoice. */
    syncVendorDetails?: boolean;
  }
) {
  return postJson(`/vendor-invoices/${id}/reconcile-vendor`, payload);
}

// The flat form fields both upload routes and the correction call read
// (services/vendorInvoicePaymentOnReceipt.readPaymentAnswer). A field a person
// left blank is not sent, so the server reads it as absent, never as "".
function paymentFields(input: PaymentOnReceiptInput): Record<string, string> {
  const fields: Record<string, string> = { paymentStatus: String(input.status ?? "") };
  if (input.paidDate) fields.paidDate = input.paidDate;
  if (input.method) fields.paymentMethod = input.method;
  if (input.reference) fields.paymentReference = input.reference;
  if (typeof input.amount === "number") fields.paidAmount = String(input.amount);
  return fields;
}

function appendPaymentAnswer(fd: FormData, input: PaymentOnReceiptInput) {
  for (const [key, value] of Object.entries(paymentFields(input))) fd.append(key, value);
}

/**
 * Give or correct the answer to "was this invoice already paid?" on an invoice
 * that has not been confirmed (D49). After Confirm & sync the bill exists and the
 * server refuses with PAYMENT_ANSWER_LOCKED: the payment is corrected in Accounts.
 */
export async function setVendorInvoicePaymentOnReceipt(id: string, input: PaymentOnReceiptInput) {
  return put<{ success: boolean; data: { paymentOnReceipt: NonNullable<VendorInvoice["paymentOnReceipt"]> } }>(
    `/vendor-invoices/${id}/payment-on-receipt`,
    paymentFields(input)
  );
}

/**
 * D94: the file held several invoices and the reviewer said YES — each becomes its
 * own record. The parent keeps only invoice 1's pages; re-read it and the list.
 */
export async function splitVendorInvoice(id: string) {
  return postJson<{ success: boolean; code: "SPLIT_DONE"; message: string; data: { parent: VendorInvoice; createdIds: string[] } }>(
    `/vendor-invoices/${id}/split`,
    {}
  );
}

/**
 * D94: the reviewer said NO — the record is DELETED (it no longer exists), and
 * each invoice has to be uploaded separately.
 */
export async function declineVendorInvoiceSplit(id: string) {
  return postJson<{ success: boolean; code: "SPLIT_DECLINED_DELETED"; message: string; data: { deleted: true } }>(
    `/vendor-invoices/${id}/decline-split`,
    {}
  );
}

/**
 * ✅ Upload a single Vendor Invoice (PDF/Image) for parsing.
 * Uses shared apiClient multipart helper so base URL + auth are consistent.
 * Backend returns { item, isDuplicate }: isDuplicate is true when this exact file
 * (by content hash) was already uploaded; `item` is then the existing invoice,
 * unchanged and not queued for reprocessing.
 */
export async function uploadVendorInvoiceSingle(args: {
  file: File;
  folderTag?: string;
  /** Required: the server refuses a file with no answer to "was this invoice already paid?" (D49). */
  payment: PaymentOnReceiptInput;
  /** D95: blank = Ship Chandlery. */
  destination?: VendorInvoiceDestination;
  signal?: AbortSignal;
}): Promise<{ item: VendorInvoice; isDuplicate: boolean }> {
  const fd = new FormData();
  fd.append("file", args.file);
  if (args.folderTag) fd.append("folderTag", args.folderTag);
  if (args.destination) fd.append("destination", args.destination);
  appendPaymentAnswer(fd, args.payment);

  const out = await postFormData<any>(`/vendor-invoices/upload-single`, fd, { signal: args.signal });

  // Backend: { item: VendorInvoice, isDuplicate: boolean }
  if (out?.item && out.item._id) {
    return { item: out.item as VendorInvoice, isDuplicate: !!out.isDuplicate };
  }

  // Fallback: if backend ever returns the invoice directly
  return { item: out as VendorInvoice, isDuplicate: false };
}

/**
 * VI-4: several photos or files that are the pages of ONE invoice. They are sent
 * in page order and the server joins them into one PDF in the order received,
 * then treats it exactly like a single upload (same `{ item, isDuplicate }`).
 * Photos should go through features/vendorInvoices/uploadPages `pageForUpload`
 * first: the server joins only PDF, JPEG and PNG.
 */
export async function uploadVendorInvoicePages(args: {
  pages: File[];
  folderTag?: string;
  /** Required, as for a single file (D49). */
  payment: PaymentOnReceiptInput;
  /** D95: blank = Ship Chandlery. */
  destination?: VendorInvoiceDestination;
  signal?: AbortSignal;
}): Promise<{ item: VendorInvoice; isDuplicate: boolean }> {
  const fd = new FormData();
  args.pages.forEach((page) => fd.append("files", page, page.name));
  if (args.folderTag) fd.append("folderTag", args.folderTag);
  if (args.destination) fd.append("destination", args.destination);
  appendPaymentAnswer(fd, args.payment);

  const out = await postFormData<{ item: VendorInvoice; isDuplicate: boolean }>(`/vendor-invoices/upload-pages`, fd, {
    signal: args.signal,
  });
  return { item: out.item, isDuplicate: !!out.isDuplicate };
}

export interface SupplierDropdownItem {
  source?: string;
  vendorName: string;
  /** D170.9: the vendor a linked bill or catalogue row is filed under (`vendorName` is its name), else null. */
  vendorId?: string | null;
  /**
   * D86: the date this PRICE counts from (invoice date, historical quote date,
   * price-list upload, catalogue row's priceDateOf), or null when none is known.
   * Never today. The wire name is historical.
   */
  invoiceDate: string | null;
  /** MasterCatalogue options only: what dated that price ('invoice' | 'price_list' | 'added' | 'price_edit'), else null. Read by `priceSourceLabel`. */
  priceDateBasis?: string | null;
  description: string;
  /**
   * The source's own stated price, or null when it states none — never 0.
   * Owner, 2026-09-24 (Cabbage White / Grapes White / Leeks White): a missing
   * historical price used to read `|| 0` on both sides of the wire and showed
   * as a real, CLICKABLE $0.00. `positiveNumber` (never a local `num()`) folds
   * a genuinely-stated $0.00 in with "no price" here too — a real product is
   * never actually free, and a row this screen offers at $0.00 is a row
   * someone can accidentally cost a line at for nothing.
   */
  unitPrice: number | null;
  /**
   * ISO code the source prices in (USD, TTD, …), or null when the source stated
   * none. Never defaulted on either side of the wire: a price whose currency is
   * unknown has to stay distinguishable from a US$ one, because applying a TT$
   * figure to a line as US$ is a ~6.8x error that is then marked up and quoted
   * (TECH_DEBT #152).
   */
  currency?: string | null;
  quantity: string;
  uom: string;
  /**
   * How this option is packed ("CASE of 12 x 3 LBS"), from its own source's
   * facts, or null when the source states none — never a pack of one. The
   * server words it (`supplierOptions.packagingOf`); the screen only prints it.
   */
  packaging?: string | null;
  /**
   * The unit `unitPrice` is per ("CASE"), as the line-unit arithmetic reads it
   * (`supplierOptions.optionUnitOf`), or null when nothing states one.
   */
  priceUnit?: string | null;
  /** On this quotation's client's preferred-vendor list. Sorted to the top. */
  preferred?: boolean;
  /**
   * D78: the supplier printed no unit price — it was worked out from the line
   * total at processing (D75) and may not have been reviewed yet. Still offered;
   * the dropdown marks it. Display only, never written to a quotation line.
   */
  unitPriceFromLineTotal?: boolean;
  /**
   * The four fields below are present only when the list was asked for one
   * line's unit (`fetchSupplierDropdownDataFromAllTables`'s `lineUom`). They are
   * the server's answer to "what does this option cost per THAT unit"
   * (BUSINESS_RULES#supplier-option-price-per-line-unit) — the figure a pick
   * writes, still in `currency`. A null `lineUnitPrice` means it cannot be
   * stated, and `lineUnitRefusal` says why. QUO-00068: TT$12.50 per 10 kg BAG
   * was applied to a KG line as TT$12.50 a kilo.
   */
  lineUnit?: string | null;
  lineUnitPrice?: number | null;
  /** 'same_unit', or the pricing basis that restated the price (e.g. 'atomic_weight'). */
  lineUnitBasis?: string | null;
  lineUnitRefusal?: string | null;
  /**
   * Present only when this option IS a Master Catalogue row (as opposed to a
   * raw vendor-invoice / historical-quotation / price-list line) — the id
   * `masterCatalogueApi.setCatalogueVat` writes to. `unitPrice`/`lineUnitPrice`
   * on such a row are already VAT-inclusive when `vatApplicable` is `true`
   * (the server applies the Settings rate) — never gross them again here.
   */
  masterCatalogueId?: string | null;
  /**
   * The catalogue row's own VAT flag, `null` when nobody has said
   * (`MasterCatalogueItem.vat_applicable`'s own doc comment). A `null` here
   * on a picked row is what opens the "Does this item have VAT?" popup
   * (`features/quotations/CatalogueVatPrompt.tsx`).
   */
  vatApplicable?: boolean | null;
}

/**
 * Fetch vendor invoices and parse them into supplier dropdown items
 * Returns line items from completed vendor invoices that match the specified item name
 * Filters to show only suppliers who have that specific item
 */
/**
 * Fetch supplier data from backend endpoint that queries all 3 sources
 * Backend queries VendorInvoices, HistoricalQuotationItems, and PriceLists directly
 */
export async function fetchSupplierDropdownDataFromAllTables(
  itemName?: string,
  quotationId?: string,
  lineUom?: string
): Promise<SupplierDropdownItem[]> {
  return (await fetchSupplierDropdown(itemName, quotationId, lineUom)).options;
}

/** The sources the server could not read (D99); the options are partial when any is named. */
export type SupplierSource = 'vendor_invoice' | 'historical_quotation' | 'price_list' | 'master_catalogue';

/**
 * One server option row to the client's shape.
 * Transform backend response to frontend format. A FIXED FIELD LIST: a field
 * the server adds and this map does not name is dropped in silence, which is
 * how the line fields below would have vanished on the way to the dropdown.
 * They are pinned in vendorInvoiceApi.test.ts for exactly that reason.
 */
function toSupplierDropdownItem(item: any, withLineUnit: boolean): SupplierDropdownItem {
  return {
    source: item.source || '',
    vendorName: item.vendorName,
    vendorId: item.vendorId ? String(item.vendorId) : null,
    invoiceDate: item.invoiceDate,
    priceDateBasis: typeof item.priceDateBasis === 'string' ? item.priceDateBasis : null,
    description: item.description,
    // `positiveNumber`, not `|| 0` — see the field's own doc comment above.
    unitPrice: positiveNumber(item.unitPrice),
    currency: item.currency ?? null,
    quantity: item.quantity || '0',
    uom: item.uom || '',
    packaging: typeof item.packaging === 'string' && item.packaging.trim() ? item.packaging : null,
    priceUnit: typeof item.priceUnit === 'string' && item.priceUnit.trim() ? item.priceUnit : null,
    preferred: item.preferred === true,
    unitPriceFromLineTotal: item.unitPriceFromLineTotal === true,
    // Read by `QuotationDetailPage.handleSupplierSelect` (ask VAT before
    // writing a blank-VAT catalogue pick) and `pendingSupplierVatPick` (re-find
    // it by id). Blank VAT stays null — null is what opens the question.
    masterCatalogueId: item.masterCatalogueId ? String(item.masterCatalogueId) : null,
    vatApplicable: typeof item.vatApplicable === 'boolean' ? item.vatApplicable : null,
    ...(withLineUnit
      ? {
          lineUnit: typeof item.lineUnit === 'string' ? item.lineUnit : null,
          lineUnitPrice: typeof item.lineUnitPrice === 'number' ? item.lineUnitPrice : null,
          lineUnitBasis: typeof item.lineUnitBasis === 'string' ? item.lineUnitBasis : null,
          lineUnitRefusal: typeof item.lineUnitRefusal === 'string' ? item.lineUnitRefusal : null,
        }
      : {}),
  };
}

export async function fetchSupplierDropdown(
  itemName?: string,
  quotationId?: string,
  /** The unit of the line the list is for — see the query below. */
  lineUom?: string
): Promise<{ options: SupplierDropdownItem[]; unavailableSources: SupplierSource[] }> {
  try {
    if (!itemName || !itemName.trim()) {
      return { options: [], unavailableSources: [] };
    }

    // quotationId is optional and only affects ORDER: the server follows it to
    // the client and floats that client's preferred suppliers to the top. Omit
    // it and the list is exactly what it has always been, so no caller breaks.
    //
    // lineUom is optional and only ADDS: the unit of the line the list is for.
    // With it the server states every option's price per that unit, or why it
    // cannot (QUO-00068); without it the list is what it has always been.
    const query = `itemName=${encodeURIComponent(itemName.trim())}${
      quotationId ? `&quotationId=${encodeURIComponent(quotationId)}` : ''
    }${lineUom !== undefined ? `&uom=${encodeURIComponent(lineUom)}` : ''}`;
    const response = await get<{
      success: boolean;
      data: any[];
      count: number;
      unavailableSources?: SupplierSource[];
    }>(`/suppliers/dropdown?${query}`);

    if (!response?.data || !Array.isArray(response.data)) {
      return { options: [], unavailableSources: [] };
    }
    const unavailableSources = Array.isArray(response.unavailableSources) ? response.unavailableSources : [];

    const options = response.data.map((item: any) => toSupplierDropdownItem(item, lineUom !== undefined));
    return { options, unavailableSources };
  } catch (error) {
    console.error('Failed to fetch supplier dropdown data from all tables:', error);
    throw error;
  }
}


/**
 * D104.20 — every line of one confirmed order, in ONE request (was one per line). The server
 * answers per item name; `unavailableSources` is shared, one load served them all.
 */
export async function fetchConfirmedOrderSupplierOptions(
  confirmedOrderId: string
): Promise<{
  optionsByItem: Record<string, SupplierDropdownItem[]>;
  unavailableSources: SupplierSource[];
  /** D105.3: the vendor register, offered on every line after its priced suppliers. */
  vendors: VendorOption[];
}> {
  const response = await get<{
    success: boolean;
    data: { optionsByItem?: Record<string, any[]>; unavailableSources?: SupplierSource[]; vendors?: VendorOption[] };
  }>(`/confirmed-orders/${encodeURIComponent(confirmedOrderId)}/supplier-options`);
  const raw = response?.data?.optionsByItem ?? {};
  const optionsByItem: Record<string, SupplierDropdownItem[]> = {};
  for (const [name, rows] of Object.entries(raw)) {
    optionsByItem[name] = Array.isArray(rows) ? rows.map((r) => toSupplierDropdownItem(r, false)) : [];
  }
  const unavailableSources = response?.data?.unavailableSources;
  const vendors = response?.data?.vendors;
  return {
    optionsByItem,
    unavailableSources: Array.isArray(unavailableSources) ? unavailableSources : [],
    vendors: Array.isArray(vendors) ? vendors.map((v) => ({ ...v, _id: String(v._id) })) : [],
  };
}
